import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, ChevronRight, ScrollText, Compass, Landmark, Telescope, ShipWheel, BookOpen, Sparkles, Leaf } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getBooks, subscribeNewsletter, mediaUrl, API } from "@/lib/api";
import { BookCover } from "@/components/BookCover";
import { TrailerEmbed } from "@/components/TrailerEmbed";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { SITE, HERO_IMAGES } from "@/data/site";
import { useHomepage, useSiteSettings } from "@/context/SiteContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PillarIcon = ({ i }) => {
  const icons = [ScrollText, Landmark, Compass, Telescope, Sparkles, ShipWheel, BookOpen, ScrollText];
  const Icon = icons[i % icons.length];
  return <Icon size={18} style={{ color: "var(--gi-maroon)" }} />;
};

const btnClass = { maroon: "btn-maroon", gold: "btn-gold", ghost: "btn-ghost", outline: "btn-outline" };

export default function Home() {
  useDocumentTitle(null);
  const homepage = useHomepage();
  const settings = useSiteSettings();
  const [books, setBooks] = useState([]);
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getBooks().then(d => { if (!cancelled) setBooks(d); }).catch(() => toast.error("Unable to load books"));
    axios.get(`${API}/content/articles`).then(r => { if (!cancelled) setArticles(r.data || []); }).catch(() => {});
    axios.get(`${API}/content/events`).then(r => { if (!cancelled) setEvents(r.data || []); }).catch(() => {});
    axios.get(`${API}/content/videos`).then(r => { if (!cancelled) setVideos(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => {
    // Prefer explicit is_featured flag; fall back to Glories of India Series titles
    const flagged = books.filter(b => b.is_featured);
    if (flagged.length) return flagged.slice(0, 4);
    return books.filter(b => (b.series || "Glories of India Series") === "Glories of India Series").slice(0, 4);
  }, [books]);
  const featuredGreen = useMemo(
    () => books.filter(b => b.series === "Green Guru Knowledge Series").slice(0, 4),
    [books],
  );

  if (!homepage) {
    return <div className="container-wide py-24 font-heading text-2xl" data-testid="home-loading">Loading…</div>;
  }
  const sectionsByKey = Object.fromEntries((homepage.sections || []).map(s => [s.key, s]));
  const ordered = [...(homepage.sections || [])].filter(s => s.enabled !== false).sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const renderers = {
    hero: (s) => <HeroSection key="hero" section={s} settings={settings} />,
    featured_books: (s) => <FeaturedBooksSection key="featured_books" section={s} books={featured} />,
    green_guru: (s) => <GreenGuruSection key="green_guru" section={s} books={featuredGreen} />,
    why: (s) => <WhySection key="why" section={s} />,
    research_approach: (s) => <ResearchApproachSection key="research_approach" section={s} />,
    author_journey: (s) => <AuthorJourneySection key="author_journey" section={s} settings={settings} />,
    watch: (s) => <WatchSection key="watch" section={s} videos={videos} />,
    join_mission: (s) => <JoinMissionSection key="join_mission" section={s} />,
    articles: (s) => <ArticlesSection key="articles" section={s} articles={articles} />,
    events: (s) => <EventsSection key="events" section={s} events={events} />,
    newsletter: (s) => <NewsletterBlock key="newsletter" section={s} />,
    final_cta: (s) => <FinalCta key="final_cta" section={s} />,
  };

  return (
    <div data-testid="home-page">
      {ordered.map(s => (renderers[s.key] ? renderers[s.key](s) : null))}
    </div>
  );
}

const SectionHeading = ({ eyebrow, title, note, inline }) => (
  <div className={inline ? "" : "text-left max-w-3xl"}>
    <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{eyebrow}</div>
    <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }}>{title}</h2>
    {note && <p className="mt-4 text-base" style={{ color: "var(--gi-muted)" }}>{note}</p>}
    <div className="hair-divider mt-6 max-w-xs" />
  </div>
);

const HeroSection = ({ section, settings }) => {
  const eyebrow = section.eyebrow || "Glories of India Series";
  const primaryImg = section.hero_primary_image_url ? mediaUrl(section.hero_primary_image_url) : HERO_IMAGES.primary;
  const manuscriptImg = section.hero_manuscript_image_url ? mediaUrl(section.hero_manuscript_image_url) : HERO_IMAGES.manuscript;
  const mapImg = section.hero_map_image_url ? mediaUrl(section.hero_map_image_url) : HERO_IMAGES.map;
  const heading = section.heading || "Rediscovering India's Civilizational Journey";
  const parts = heading.split(/<br\s*\/?>/i);
  const ctas = section.ctas || [];
  return (
    <section className="container-wide pt-16 sm:pt-24 pb-20 grid gap-16 lg:grid-cols-[1.05fr_1fr] items-center" data-testid="section-hero">
      <div className="reveal">
        <div className="font-accent text-[10px] mb-6" style={{ color: "var(--gi-gold)" }} data-testid="hero-eyebrow">{eyebrow}</div>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }} data-testid="hero-headline">
          {parts[0]}{parts[1] && <><br /><span style={{ color: "var(--gi-charcoal)" }}>{parts[1]}</span></>}
        </h1>
        <div className="hair-divider my-10 max-w-md" />
        <p className="text-lg leading-relaxed max-w-xl" style={{ color: "var(--gi-charcoal-2)" }} data-testid="hero-subheading">{section.subheading}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          {ctas.map((c, i) => (
            c.to?.startsWith("#") ? (
              <a key={`cta-${i}`} href={c.to} className={btnClass[c.variant] || "btn-ghost"} data-testid={`hero-cta-${i}`}>
                {c.variant === "ghost" && <PlayCircle size={16} />} {c.label}
              </a>
            ) : (
              <Link key={`cta-${i}`} to={c.to} className={btnClass[c.variant] || "btn-ghost"} data-testid={`hero-cta-${i}`}>
                {c.label} <ArrowRight size={14} />
              </Link>
            )
          ))}
        </div>
      </div>
      <div className="reveal reveal-delay-1 relative">
        <div className="hero-montage">
          <img src={primaryImg} alt={section.hero_primary_image_alt || "Hero image"} />
          <div className="overlay" />
          <div className="caption">{settings?.publisher_name || SITE.name}</div>
        </div>
      </div>
    </section>
  );
};

const FeaturedBooksSection = ({ section, books }) => (
  <>
    <div className="container-wide">
      <SectionHeading eyebrow={section.eyebrow} title={section.heading} note={section.note} />
    </div>
    <section className="container-wide pb-24">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 mt-10" data-testid="featured-books-grid">
        {books.map((b) => <FeaturedBookCard key={b.id} book={b} />)}
      </div>
      <div className="mt-14 flex justify-center">
        <Link to="/books" className="btn-outline" data-testid="view-all-books-btn"><T textKey="home.viewall.books" fallback="View All Titles" /> <ArrowRight size={14} /></Link>
      </div>
    </section>
  </>
);

const GreenGuruSection = ({ section, books }) => (
  <section className="py-24 paper-noise" style={{ background: "var(--gi-green-soft)" }} data-testid="green-guru-section">
    <div className="container-wide">
      <div className="flex items-center gap-3">
        <Leaf size={16} style={{ color: "var(--gi-green)" }} />
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-green)", letterSpacing: "0.22em" }}>{section.eyebrow}</div>
      </div>
      <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "var(--gi-green)" }}>{section.heading}</h2>
      <p className="mt-6 max-w-2xl text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{section.body}</p>
      <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="green-guru-grid">
        {books.map(b => <FeaturedBookCard key={b.id} book={b} greenAccent />)}
      </div>
      <div className="mt-12">
        <Link to={section.cta_to || "/books"} className="inline-flex items-center gap-2 px-6 py-3 border transition-colors" style={{ background: "var(--gi-green)", color: "#FFFFFF", borderColor: "var(--gi-green)", fontSize: 12, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase" }} data-testid="view-all-green-guru-btn">
          {section.cta_label} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </section>
);

const WhySection = ({ section }) => (
  <section className="py-24" style={{ background: "var(--gi-ivory-2)" }} data-testid="section-why">
    <div className="container-wide">
      <SectionHeading eyebrow={section.eyebrow} title={section.heading} inline />
      <p className="max-w-2xl mt-6 text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{section.body}</p>
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {(section.cards || []).map((c) => (
          <div key={c.index || c.title} className="bento-card paper-noise" data-testid={`why-card-${c.index || c.title}`}>
            <div className="index">{c.index}</div>
            <h3 className="font-heading text-2xl mt-4" style={{ color: "var(--gi-maroon)" }}>{c.title}</h3>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ResearchApproachSection = ({ section }) => (
  <section className="container-wide py-28" data-testid="section-research">
    <SectionHeading eyebrow={section.eyebrow} title={section.heading} />
    <div className="mt-14 grid gap-x-16 gap-y-10 md:grid-cols-2">
      {(section.pillars || []).map((p, i) => (
        <div key={p.title} className="pillar" data-testid={`pillar-${i}`}>
          <div className="flex items-center gap-3">
            <PillarIcon i={i} />
            <h4 className="font-heading text-2xl" style={{ color: "var(--gi-charcoal)" }}>{p.title}</h4>
          </div>
          <p className="mt-2 text-sm" style={{ color: "var(--gi-muted)" }}>{p.note}</p>
        </div>
      ))}
    </div>
    {section.disclaimer && (
      <div className="mt-16 p-8 border-l-2" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)" }} data-testid="research-disclaimer">
        <div className="font-accent text-[10px] mb-3" style={{ color: "var(--gi-maroon)" }}>Editorial Disclaimer</div>
        <p className="text-sm leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{section.disclaimer}</p>
      </div>
    )}
  </section>
);

const AuthorJourneySection = ({ section, settings }) => {
  const founder = settings?.founder_name || SITE.founder;
  const role = settings?.founder_role || "Author, environmentalist and independent researcher";
  const portrait = section.portrait_url ? mediaUrl(section.portrait_url) : null;
  return (
    <section className="py-28" style={{ background: "var(--gi-ivory-2)" }} data-testid="section-author-journey">
      <div className="container-wide grid gap-16 lg:grid-cols-[1fr_1.1fr] items-start">
        <div className="relative">
          <div className="aspect-[4/5] border overflow-hidden" style={{ borderColor: "var(--gi-border)", background: "var(--gi-sandstone)" }} data-testid="author-portrait">
            {portrait ? (
              <img src={portrait} alt={`Portrait of ${founder}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
                <div className="font-heading text-3xl" style={{ color: "var(--gi-maroon)" }}>{founder}</div>
                <div className="mt-2 text-xs" style={{ color: "var(--gi-muted)" }}>{role}</div>
              </div>
            )}
          </div>
        </div>
        <div>
          <SectionHeading eyebrow={section.eyebrow} title={section.heading} />
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{section.body}</p>
          <div className="mt-10 relative">
            <div className="timeline-line" />
            {(section.nodes || []).map((n) => (
              <div key={n.title} className="timeline-node py-4">
                <div className="font-heading text-xl" style={{ color: "var(--gi-charcoal)" }}>{n.title}</div>
                <div className="mt-1 text-sm" style={{ color: "var(--gi-muted)" }}>{n.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/about" className="btn-maroon" data-testid="author-story-btn"><T textKey="home.author.read_story" fallback="Read the Author's Story" /> <ArrowRight size={14} /></Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const WatchSection = ({ section, videos }) => {
  // Only show cards that are Published AND have a valid YouTube URL.
  const isPublished = (v) => (v?.publish_status || "").toLowerCase() === "published";
  const YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const hasYouTubeUrl = (v) => YT_RE.test(v?.trailer_url || v?.url || v?.external_url || "");
  const published = (videos || [])
    .filter(v => isPublished(v) && hasYouTubeUrl(v))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  // Rule: hide the entire section when no valid published videos exist.
  if (published.length === 0) return null;

  return (
    <section id="watch" className="container-wide py-28" data-testid="section-watch">
      <SectionHeading eyebrow={section.eyebrow} title={section.heading} />
      {section.body && <p className="mt-6 max-w-2xl text-base" style={{ color: "var(--gi-charcoal-2)" }}>{section.body}</p>}
      <div className="mt-14 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <TrailerEmbed
            url={published[0].trailer_url || published[0].url || published[0].external_url}
            thumbnail={published[0].thumbnail_url}
            title={published[0].title}
          />
          <div className="mt-3 font-heading text-lg" style={{ color: "var(--gi-charcoal)" }}>{published[0].title}</div>
        </div>
        {published.length > 1 && (
          <div className="grid grid-cols-2 gap-6">
            {published.slice(1, 5).map((v) => (
              <div key={v.id}>
                <TrailerEmbed url={v.trailer_url || v.url || v.external_url} thumbnail={v.thumbnail_url} title={v.title} />
                <div className="mt-2 text-xs" style={{ color: "var(--gi-charcoal)" }}>{v.title}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const JoinMissionSection = ({ section }) => (
  <section className="py-28" style={{ background: "var(--gi-ivory-2)" }} data-testid="section-join-mission">
    <div className="container-wide">
      <SectionHeading eyebrow={section.eyebrow} title={section.heading} />
      <p className="mt-6 max-w-3xl text-lg leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{section.body}</p>
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {(section.cards || []).map(p => (
          <Link to={`/join-the-movement?path=${p.id}`} key={p.id} className="participation-card" data-testid={`participation-${p.id}`}>
            <div className="font-accent text-[10px]" style={{ color: "var(--gi-maroon)" }}>{p.index}</div>
            <h3 className="mt-4 font-heading text-2xl" style={{ color: "var(--gi-charcoal)" }}>{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{p.body}</p>
            <div className="mt-6 font-accent text-[10px] flex items-center gap-2" style={{ color: "var(--gi-maroon)" }}><T textKey="common.learn_more" fallback="Learn more" /> <ArrowRight size={12} /></div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ArticlesSection = ({ section, articles }) => {
  const published = (articles || []).slice(0, 3);
  // Rule: hide the entire section when no articles are published.
  if (published.length === 0) return null;
  return (
    <section className="container-wide py-28" data-testid="section-articles">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <SectionHeading eyebrow={section.eyebrow} title={section.heading} inline />
        <Link to="/articles" className="btn-ghost" data-testid="articles-view-all"><T textKey="common.view_all" fallback="View all" /> <ArrowRight size={12} /></Link>
      </div>
      <div className="mt-14 grid gap-8 md:grid-cols-3">
        {published.map(a => {
          const hasBody = Boolean((a.body || "").trim() || (a.summary || "").trim());
          const inner = (
            <>
              <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{a.category || "Article"}</div>
              <h3 className="mt-4 font-heading text-xl" style={{ color: "var(--gi-charcoal)" }}>{a.title}</h3>
              <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>{a.author || ""}{a.reading_time ? ` · ${a.reading_time}` : ""}</p>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{a.summary || a.body?.slice(0, 200)}</p>
            </>
          );
          return hasBody && a.slug ? (
            <Link key={a.id} to={`/articles/${a.slug}`} className="block bento-card hover:opacity-90 transition-opacity" data-testid={`article-card-${a.slug || a.id}`}>{inner}</Link>
          ) : (
            <article key={a.id} className="bento-card" data-testid={`article-card-${a.slug || a.id}`}>{inner}</article>
          );
        })}
      </div>
    </section>
  );
};

const EventsSection = ({ section, events }) => {
  const published = (events || []).slice(0, 3);
  // Rule: hide the entire section when no events are published.
  if (published.length === 0) return null;
  return (
    <section className="py-24" style={{ background: "var(--gi-ivory-2)" }} data-testid="section-events">
      <div className="container-wide">
        <SectionHeading eyebrow={section.eyebrow} title={section.heading} />
        <div className="mt-12 border-t" style={{ borderColor: "var(--gi-divider)" }}>
          {published.map(e => {
            const d = e.starts_at ? new Date(e.starts_at) : null;
            return (
              <div key={e.id} className="grid gap-8 sm:grid-cols-[140px_1fr_auto] py-8 border-b items-center" style={{ borderColor: "var(--gi-divider)" }} data-testid={`event-card-${e.slug || e.id}`}>
                <div>
                  <div className="font-heading text-5xl leading-none" style={{ color: "var(--gi-maroon)" }}>{d ? d.getDate() : "—"}</div>
                  <div className="font-accent text-[10px] mt-2" style={{ color: "var(--gi-muted)" }}>{d ? d.toLocaleString(undefined, { month: "short", year: "numeric" }) : "Date TBC"}</div>
                </div>
                <div>
                  <h3 className="font-heading text-2xl" style={{ color: "var(--gi-charcoal)" }}>{e.title}</h3>
                  <p className="mt-1 text-sm" style={{ color: "var(--gi-muted)" }}>{e.location || (e.is_online ? "Online" : "TBA")}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCta = ({ section }) => (
  <section className="container-wide py-32 text-center" data-testid="section-final-cta">
    <div className="font-accent text-[10px] mb-6" style={{ color: "var(--gi-gold)" }}>{section.eyebrow}</div>
    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl mx-auto" style={{ color: "var(--gi-maroon)" }} data-testid="final-cta-headline">
      {section.heading}
    </h2>
    <div className="mt-12 flex flex-wrap gap-4 justify-center">
      {(section.ctas || []).map((c, i) => (
        <Link key={`fcta-${i}`} to={c.to} className={btnClass[c.variant] || "btn-outline"} data-testid={`final-cta-${i}`}>{c.label}</Link>
      ))}
    </div>
  </section>
);

const FeaturedBookCard = ({ book, greenAccent }) => {
  const accentColor = greenAccent ? "var(--gi-green)" : "var(--gi-gold)";
  return (
    <Link to={`/books/${book.slug}`} className="block group" data-testid={`featured-book-${book.slug}`}>
      <BookCover title={book.title} accent={book.cover_accent} series={book.series} coverUrl={book.cover_url} />
      <div className="mt-5">
        <div className="font-accent text-[9px]" style={{ color: accentColor }}>{book.status}</div>
        <div className="mt-2 font-heading text-xl leading-tight group-hover:underline" style={{ color: "var(--gi-charcoal)" }}>{book.title}</div>
        <div className="mt-1 text-xs" style={{ color: "var(--gi-muted)" }}>{book.editions?.join(" · ")}</div>
      </div>
    </Link>
  );
};

const VideoPlaceholder = ({ title, aspect, small, testId }) => (
  <div className="relative border overflow-hidden group cursor-pointer" style={{ borderColor: "var(--gi-border)", background: "var(--gi-sandstone)", aspectRatio: aspect }} data-testid={testId}>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <PlayCircle size={small ? 32 : 48} style={{ color: "var(--gi-maroon)" }} className="transition-transform group-hover:scale-110" />
      <div className="mt-3 font-heading text-lg" style={{ color: "var(--gi-charcoal)" }}>{title}</div>
      <div className="mt-1 font-accent text-[9px]" style={{ color: "var(--gi-muted)" }}>Video · Editable Placeholder</div>
    </div>
  </div>
);

const NewsletterBlock = ({ section }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", country: "", language: "English", interests: [], consent: false });
  const [loading, setLoading] = useState(false);
  const toggleInterest = (i) => setForm(f => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter(x => x !== i) : [...f.interests, i] }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to subscribe.");
    setLoading(true);
    try {
      await subscribeNewsletter(form);
      toast.success(t("news.success", "Thank you. Please expect updates from the Glories of India desk."));
      setForm({ name: "", email: "", country: "", language: "English", interests: [], consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed. Please try again.");
    } finally { setLoading(false); }
  };
  return (
    <section className="newsletter-block py-28 paper-noise" data-testid="newsletter-section">
      <div className="container-wide grid gap-14 lg:grid-cols-[1fr_1.1fr] items-start">
        <div>
          <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{section.eyebrow}</div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.05]">{section.heading}</h2>
          <div className="hair-divider mt-8 max-w-xs" style={{ background: "linear-gradient(90deg, transparent, rgba(200,169,81,0.4) 20%, rgba(200,169,81,0.4) 80%, transparent)" }} />
          <p className="mt-8 text-lg leading-relaxed" style={{ color: "rgba(250,249,246,0.85)" }}>{section.body}</p>
        </div>
        <form onSubmit={submit} className="grid gap-5" data-testid="newsletter-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="gi-label"><T textKey="news.name" fallback="Name" /></label>
              <input className="gi-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="newsletter-name" />
            </div>
            <div>
              <label className="gi-label"><T textKey="news.email" fallback="Email" /></label>
              <input type="email" className="gi-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="newsletter-email" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="gi-label"><T textKey="news.country" fallback="Country" /></label>
              <input className="gi-input" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="newsletter-country" />
            </div>
            <div>
              <label className="gi-label"><T textKey="news.language" fallback="Preferred Language" /></label>
              <select className="gi-input" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} data-testid="newsletter-language">
                <option>English</option><option>Hindi</option><option>Bengali</option>
              </select>
            </div>
          </div>
          <div>
            <label className="gi-label"><T textKey="news.interests" fallback="Areas of Interest" /></label>
            <div className="flex flex-wrap gap-2">
              {["Archaeology", "Ancient Texts", "Chronology", "Field Journeys", "Public Awareness"].map(i => (
                <button type="button" key={i} onClick={() => toggleInterest(i)} className="px-3 py-1.5 text-xs border transition-colors"
                  style={{
                    borderColor: form.interests.includes(i) ? "var(--gi-gold)" : "rgba(255,255,255,0.24)",
                    background: form.interests.includes(i) ? "rgba(200,169,81,0.18)" : "transparent",
                    color: "var(--gi-ivory)"
                  }}
                  data-testid={`newsletter-interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                >{i}</button>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-3 text-sm" style={{ color: "rgba(250,249,246,0.85)" }}>
            <input type="checkbox" checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })} className="mt-1" data-testid="newsletter-consent" />
            <span><T textKey="news.consent" fallback="I consent to receive occasional updates." /></span>
          </label>
          <div>
            <button type="submit" disabled={loading} className="btn-gold" data-testid="newsletter-submit">
              {loading ? <T textKey="common.subscribing" fallback="Subscribing…" /> : <T textKey="common.subscribe" fallback="Subscribe" />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
